import React from "react"
import { useRouter } from "next/router"
import styled from "styled-components"
import { useTranslation } from "next-i18next"
import Button from "@/common/components/_UI/Button"
import Heading from "@/common/components/_UI/Heading"
import TabSection from "@/common/components/TabSection"
import OpCards from "./OpCards"

const OPDATA_en = [
  {
    id: "op1",
    title: "Software Engineer - Backend",
    desc: "Develop high-quality, high-concurrency, high-availability, high-scalability API backend with distributed micro-services",
    tab: ["all", "Developer"],
    content: [
      {
        title: "Responsibilities",
        content: (
          <ul>
            <li>
              Develop high-quality, high-concurrency, high-availability,
              high-scalability API backend with distributed micro-services.
            </li>
            <li>
              Develop KryptoGO services, including RESTful API and web socket.
            </li>
            <li>
              Design and build a data platform to store, process, and query
              petabytes of data.
            </li>
            <li>
              Build integrations with various public and private data sources,
              including various blockchains.
            </li>
            <li>
              Develop a deep understanding of blockchain technology in order to
              inform data architecture.
            </li>
            <li>
              Design and operation of high performance distributed systems.
            </li>
          </ul>
        )
      },
      {
        title: "Requirements",
        content: (
          <ul>
            <li>3+ years backend experiences.</li>
            <li>
              Experience in Golang, Java, C/C++, Python or other equivalent
              languages.
            </li>
            <li>SQL related experience.</li>
            <li>Knowledge in protocols (TCP/IP/HTTP/HTTPS).</li>
            <li>Ability to write high quality production code.</li>
          </ul>
        )
      },
      {
        title: "Nice to have",
        content: (
          <ul>
            <li>
              Knowledge of Blockchain/ Bitcoin/ Ethereum/ Crypto/
              Cryptocurrency.
            </li>
            <li>
              Good understanding of a broad spectrum of traditional RDBMS,
              NoSQL, and graph data stores including PostGres, MongoDB, Redis,
              and Neo4j.
            </li>
            <li>
              Experience with data pipeline and workflow management tools:
              Azkaban, Luigi, Airflow, Storm, etc.
            </li>
            <li>
              Experience with big data open source technologies like MapReduce,
              Spark, Flink, HBase and Hive.
            </li>
          </ul>
        )
      }
    ]
  },
  {
    id: "op2",
    title: "Software Engineer - Machine Learning",
    desc: "Use AI-calculated credit rating to achieve customer segmentation and rank-ordering of AML alarms.",
    tab: ["all", "Developer"],
    content: [
      {
        title: "Responsibilities",
        content: (
          <ul>
            <li>
              Design and implement models to cluster token holders to enable
              more intelligent user segmentation.
            </li>
            <li>
              Identify suspicious transactions to help our customers reduce
              legal risk.
            </li>
            <li>
              Determine high-potential user segments to help our customers grow
              faster.
            </li>
            <li>
              Collaborate with backend engineers to productionize machine
              learning models.
            </li>
            <li>
              Develop a deep understanding of blockchain technology to inform
              model design.
            </li>
            <li>
              Stay current with blockchain analytics trends to inform product
              direction.
            </li>
            <li>
              Write technical blog articles to showcase our machine learning
              work to a broader audience.
            </li>
          </ul>
        )
      },
      {
        title: "Requirements",
        content: (
          <ul>
            <li>3+ years of industry experience or a PhD.</li>
            <li>
              Strong engineering skills. Ability to write high quality
              production code. Experience in Java, Python and other equivalent
              languages is a plus.
            </li>
            <li>
              Good understanding of common families of models, feature
              engineering, feature selection, and other practical machine
              learning topics such as overfitting.
            </li>
            <li>
              Experience using open source machine learning frameworks such as
              Scikit-Learn, H2O, Kera, TensorFlow.
            </li>
          </ul>
        )
      },
      {
        title: "Nice to have",
        content: (
          <ul>
            <li>
              Knowledge of Blockchain/ Bitcoin/ Ethereum/ Crypto/
              Cryptocurrency.
            </li>
            <li>
              Experience with big data open source technologies like MapReduce,
              Spark, Flink, HBase and Hive.
            </li>
          </ul>
        )
      }
    ]
  },
  {
    id: "op3",
    title: "Software Engineer - Frontend",
    desc: "Develop high quality websites for KryptoGO.",
    tab: ["all", "Developer"],
    content: [
      {
        title: "Responsibilities",
        content: (
          <ul>
            <li>
              Design, develop, and maintain modern front-end applications.
            </li>
            <li>
              Work with design team to ensure the best user experience and
              website performance.
            </li>
            <li>
              Use best front-end development practices to create efficient and
              scalable code.
            </li>
            <li>
              Write UI test and end-to-end test to improve overall quality and
              enhance testability.
            </li>
          </ul>
        )
      },
      {
        title: "Job Requirements",
        content: (
          <ul>
            <li>2+ years of front-end development experience.</li>
            <li>
              Proficient in modern front-end frameworks such as React, Next JS.
            </li>
            <li>
              Familiar with back-end technologies such as RESTful APIs, Web
              socket, Server sent events.
            </li>
            <li>
              Proficient in front-end technologies such as Typescript, styled
              component, Tailwind.
            </li>
            <li>
              Familiar with unit test, UI test, e2e test tools such as Jest,
              Mocha, Chai, Cypress, Browserstack, PlayWright.
            </li>
            <li>Excellent communication and teamwork skills.</li>
          </ul>
        )
      },
      {
        title: "Preferred Qualifications",
        content: (
          <ul>
            <li>
              Experience with related technologies such as Webpack, esbuild,
              babel, React 18, React server component, Nextjs app directory
            </li>
            <li>
              Experience with monorepo related technologies such as Nx,
              Turborepo
            </li>
            <li>Experience with continuous integration and deployment</li>
            <li>Experience with front-end performance optimization, SEO</li>
            <li>Experience with Docker</li>
            <li>
              Experience with Blockchain: ethers.js, smart contract development
            </li>
          </ul>
        )
      }
    ]
  },
  {
    id: "op3-1",
    title: "Senior Software Engineer - Flutter",
    tab: ["all", "Developer"],
    content: [
      {
        title: "Responsibilities",
        content: (
          <ul>
            <li>Develop KryptoGO wallet: https://kryptogo.com/wallet</li>
            <li>
              Build integrations with various public and private data sources,
              including full node integration.
            </li>
            <li>Build a CI/CD flow for mobile development.</li>
          </ul>
        )
      },
      {
        title: "Requirements",
        content: (
          <ul>
            <li>Have at least one iOS/Android apps developed with Flutter.</li>
            <li>3+ years in mobile software development.</li>
            <li>Over one year developing Flutter applications for mobile.</li>
            <li>Experience with third-party libraries and APIs.</li>
            <li>Understanding of the Agile development life-cycle.</li>
            <li>Experience with automated testing and building.</li>
          </ul>
        )
      },
      {
        title: "Nice to have",
        content: (
          <ul>
            <li>
              Knowledge of Blockchain/ Bitcoin/ Ethereum/ Crypto/
              Cryptocurrency.
            </li>
          </ul>
        )
      }
    ]
  },
  {
    id: "op4",
    title: "Software Engineer - iOS Developer",
    desc: "Design and build applications for the iOS platform",
    tab: ["all", "Developer"],
    content: [
      {
        title: "Responsibilities",
        content: (
          <ul>
            <li>Design and build applications for the iOS platform</li>
            <li>
              Ensure the performance, quality, and responsiveness of
              applications
            </li>
            <li>
              Collaborate with a team to define, design, and ship new features
            </li>
            <li>Identify and correct bottlenecks and fix bugs</li>
            <li>
              Help maintain code quality, organization, and automatization
            </li>
          </ul>
        )
      },
      {
        title: "Essential Requirements",
        content: (
          <ul>
            <li>Proficient with Objective-C or Swift, and Cocoa Touch</li>
            <li>
              Experience with iOS frameworks such as Core Data, Core Animation,
              etc.
            </li>
            <li>
              Familiarity with RESTful APIs to connect iOS applications to
              back-end services
            </li>
            <li>Knowledge of other web technologies and UI/UX standards</li>
            <li>
              Understanding of Apple’s design principles and interface
              guidelines
            </li>
            <li>Proficient understanding of code versioning tools</li>
            <li>Familiarity with continuous integration</li>
            <li>
              A commitment to collaborative problem solving, sophisticated
              design, and the creation of quality products
            </li>
          </ul>
        )
      }
    ]
  },
  {
    id: "op5",
    title: "Software Engineer - Android Developer",
    desc: "Design and build applications for the Android platform",
    tab: ["all", "Developer"],
    content: [
      {
        title: "Responsibilities",
        content: (
          <ul>
            <li>Design and build applications for the Android platform.</li>
            <li>
              Ensure the performance, quality, and responsiveness of
              applications.
            </li>
            <li>
              Collaborate with a team to define, design, and ship new features.
            </li>
            <li>Identify and correct bottlenecks and fix bugs.</li>
            <li>
              Help maintain code quality, organization, and automatization.
            </li>
          </ul>
        )
      },
      {
        title: "Essential Requirements",
        content: (
          <ul>
            <li>2+ years of Android app experience.</li>
            <li>Proficient with Java. Kotlin will be a strong plus.</li>
            <li>
              Familiar with RESTful APIs and WebSocket to connect to back-end
              services.
            </li>
            <li>Familiar with asynchronous programming, e.g. ReactiveX.</li>
            <li>
              Knowledge of other web technologies and Material design principles
              will also be a plus.
            </li>
            <li>Passionate with new technologies.</li>
            <li>Familiarity with continuous integration</li>
            <li>
              A commitment to collaborative problem solving, sophisticated
              design, and the creation of quality products.
            </li>
          </ul>
        )
      }
    ]
  },
  {
    id: "op6",
    title: "Site Reliability Engineer",
    desc: "Contribute to the release and change of the management process with developers and operations",
    tab: ["all", "Developer"],
    content: [
      {
        title: "Responsibilities",
        content: (
          <ul>
            <li>
              Solve configuration management, authentication, and scaling
              problems
            </li>
            <li>
              Operate and improve monitoring clusters, including patching and
              fixing bugs
            </li>
            <li>Collaborate with the others to deliver monitoring solutions</li>
            <li>Develop and maintain cloud computing services</li>
            <li>Manage the workflow of continuous integration (CI)</li>
            <li>
              Evaluate release processes and tools for advanced improvement
            </li>
            <li>
              Contribute to the release and change of the management process
              with developers and operations
            </li>
          </ul>
        )
      },
      {
        title: "Essential Requirements",
        content: (
          <ul>
            <li>
              Bachelor&apos;s degree in computer science/engineering or any
              related field or equivalent practical experience
            </li>
            <li>
              Ability to use a wide variety of open-source technologies and
              automation tools
            </li>
            <li>Experience with systems and IT operations</li>
            <li>
              Comfortable with frequent, incremental code testing and
              deployment.
            </li>
            <li>
              Comfortable with collaboration, open communication, and reaching
              across functional borders
            </li>
            <li>
              Unix-like (Linux or FreeBSD) system administration and programming
              experience, able to automate tasks with (shell) scripts
            </li>
            <li>Accomplish assigned projects under minimal supervision</li>
            <li>Ability to use configuration management tools and Git</li>
            <li>
              Solid experience in operating system automation, knowledge in
              Puppet, Chef, Salt, and Ansible
            </li>
          </ul>
        )
      }
    ]
  },
  {
    id: "op9",
    title: "Quality Assurance",
    desc: "Develop advanced machine learning and statistical models",
    tab: ["all", "Developer"],
    content: [
      {
        title: "Responsibilities",
        content: (
          <ul>
            <li>Execute test cases and analyse test results.</li>
            <li>Preparing test reports for tracking and analysis.</li>
            <li>Evaluate test cases and explore new use cases.</li>
            <li>Report bugs and improvements to development team.</li>
            <li>Troubleshoot issues.</li>
            <li>Conduct new feature and post-release testing.</li>
            <li>
              Work with cross-functional teams to ensure quality throughout the
              software development life cycle.
            </li>
          </ul>
        )
      },
      {
        title: "Requirements",
        content: (
          <ul>
            <li>CS/information major is a plus.</li>
            <li>Logical, detail-oriented, and sensitive to numbers.</li>
            <li>Excellent skills in analyzing.</li>
            <li>
              Ability to comprehensively understand data sources, elements and
              relationships in both business and technical terms.
            </li>
            <li>Ability to troubleshoot and document errors.</li>
            <li>
              Analytical and curios mindset with a problem-solving aptitude.
            </li>
            <li>
              Experience in QA, Agile framework, or project management a huge
              plus.
            </li>
          </ul>
        )
      }
    ]
  },
  {
    id: "op10",
    title: "Product Manager",
    desc: "Contribute to the development of the overall product strategies, product roadmaps, and business plans.",
    tab: ["all", "Developer"],
    content: [
      {
        title: "Responsibilities",
        content: (
          <ul>
            <li>
              Contribute to the development of the overall product strategies,
              product roadmap, and business plans.
            </li>
            <li>
              Act as a product evangelist to build awareness and understanding.
            </li>
            <li>Global Cryptocurrency research.</li>
            <li>
              Identify needs through a combination of user research, collecting
              feedback from cross-functional teammates, diving into data, and
              competitive analysis.
            </li>
            <li>
              Collect and respond to user feedbacks on Google Play/App Store of
              our product.
            </li>
          </ul>
        )
      },
      {
        title: "Requirements",
        content: (
          <ul>
            <li>3+ years of experience in a PM role.</li>
            <li>BS or BA Degree/ Technical background preferred.</li>
            <li>Strong analytical skills.</li>
            <li>Excellent organizational and communication skills.</li>
            <li>
              Ability to comprehensively understand data sources, elements and
              relationships in both business and technical terms.
            </li>
            <li>
              Detail-oriented and able to work on multiple tasks autonomously.
            </li>
            <li>
              Passion & Knowledge of cryptocurrency and blockchain technologies
              is a must.
            </li>
          </ul>
        )
      }
    ]
  },
  {
    id: "op11",
    title: "Business Development Manager",
    desc: "Drive vision, strategy and results through personal and team sales activity",
    tab: ["all", "BD"],
    content: [
      {
        title: "Responsibilities",
        content: (
          <ul>
            <li>
              Drive vision, strategy and results through personal and team sales
              activity
            </li>
            <li>Convert sales funnel into commercial success</li>
            <li>Drive greater results from a growing sales function</li>
            <li>
              Support strategy for KryptoGO to attract users that need to
              monitor and secure DeFi applications and crypto assets
            </li>
            <li>
              Help KryptoGO become laser focused on their market and product
              strategy which includes presentation decks and messaging.
            </li>
            <li>
              Advise Marketing team with planning and strategies for campaign
              ideation, development and execution.
            </li>
            <li>
              Strategy sessions with the Founder and executives to ensure clear
              focus and milestones
            </li>
          </ul>
        )
      },
      {
        title: "Requirements",
        content: (
          <ul>
            <li>
              3+ years experience in partnerships or business development (with
              at least 2 of the years at a software or technology company)
            </li>
            <li>Understanding of Fintech, Web3 culture</li>
            <li>
              Experience in recruiting, coaching and building early stage teams
            </li>
            <li>
              Passionate about building new businesses and willing to be
              hands-on to get the job done
            </li>
            <li>
              Outstanding interpersonal skills, both within your team and with
              leadership and partners
            </li>
            <li>Be an analytical thinker who loves to solve problems</li>
            <li>
              Be an owner and proactively find solutions to business problems
            </li>
            <li>
              You are organized. You deliver stuff on time & handle stress well
            </li>
            <li>Excellent communication & written skills in English</li>
          </ul>
        )
      },
      {
        title: "Desirable Requirements",
        content: (
          <ul>
            <li>1+ years experience in leadership and team management roles</li>
            <li>Strong understanding of digital currencies/assets.</li>
            <li>Pragmatic knowledge of international payments.</li>
            <li>Deep insight into commodities/currencies or bitcoin.</li>
            <li>Familiar with Psychology is a plus.</li>
            <li>Outstanding experience of public sector is a plus.</li>
            <li>Crypto Degen and passionate about Crypto.</li>
          </ul>
        )
      }
    ]
  },
  {
    id: "op12",
    title: "Partnership Manager",
    desc: "You’ll obligate to be in high demand of connecting with strategic partners to establish and maintain business operations.",
    tab: ["all", "BD"],
    content: [
      {
        title: "Responsibilities",
        content: (
          <ul>
            <li>
              This role is crucial for KryptoGO to maintain business growth with
              the ambition of rapid expansions. It would be a strategic liaison
              for KryptoGO to operate with peripheral institutes and ecosystem
              partners for corporate strategy. You’ll obligate to be in high
              demand of connecting with strategic partners to establish and
              maintain business operations. You’ll continue to develop your
              views across various levels with KryptoGO.
            </li>
          </ul>
        )
      },
      {
        title: "Requirements",
        content: (
          <ul>
            <li>
              3+ years experience in engaging with influencers in different
              fields.
            </li>
            <li>
              Timely perceive and understand up-to-date knowledge, product and
              service offerings of cryptocurrency and blockchain tech.
            </li>
            <li>
              To deliver excellent interpersonal skills, international
              communication and travels.
            </li>
            <li>
              To conduct quick approach, collection, maintenance of critical
              connections.
            </li>
          </ul>
        )
      },
      {
        title: "Desirable Requirements",
        content: (
          <ul>
            <li>Strong understanding of digital currencies/assets.</li>
            <li>Pragmatic knowledge of international payments.</li>
            <li>Deep insight into commodities/currencies or bitcoin.</li>
            <li>Familiar with Psychology is a plus.</li>
            <li>Outstanding experience of public sector is a plus.</li>
          </ul>
        )
      }
    ]
  },
  {
    id: "op13",
    title: "Product Marketing Manager",
    desc: "We are looking for a candidate who has great insights in the blockchain or internet application industry to boost our brand awareness globally.",
    tab: ["all", "BD"],
    content: [
      {
        title: "Foreigners are welcome",
        content: (
          <ul>
            <li>
              We are in need of a Product Marketing Manager who is equipped with
              both technical knowledge and project management skills. The
              candidate will collaborate with two departments to ensure the
              product and technology in the blockchain field is developing
              insightfully. As the nature of the industry changes significantly
              and rapidly, the candidate must keep pace on the newest trend and
              alteration of industry events. The candidate will work under the
              marketing team to keep the latest trend and preferences of our
              target audience updated. Being keen, detail oriented, and flexible
              is critical for success in this position. We are looking for a
              candidate who has great insights in the blockchain or internet
              application industry to boost our brand awareness globally. The
              ideal candidate will be result-oriented and comfortable working in
              a collaborative environment.
            </li>
          </ul>
        )
      },
      {
        title: "Responsibilities",
        content: (
          <ul>
            <li>
              Develop overall marketing strategies and tactics and drive
              qualified traffic to the front door.
            </li>
            <li>
              Define and implement a comprehensive go-to-market strategic
              direction and plans, identifying key messages and tactics to
              support the launch of new product feature initiatives and drive
              the highest impact marketing; manage the cross-functional
              implementation of the plan.
            </li>
            <li>
              Build marketing programs to support specific marketing objectives
              across different channels and segments in support of our overall
              strategic marketing plan.
            </li>
            <li>
              Collaborate with Channel Marketing team members to deliver
              successful messaging.
            </li>
            <li>
              Measure and report performance of marketing campaigns, gain
              insight, and assess against goals.
            </li>
            <li>
              Work closely with Product and Engineering Team to identify needs
              and sharpen product offerings based on feedback from customers and
              competitive positioning.
            </li>
            <li>
              Build strategic relationships and partnerships with key industry
              players, agencies and vendors.
            </li>
          </ul>
        )
      },
      {
        title: "Requirements",
        content: (
          <ul>
            <li>Bachelor’s degree or equivalent in marketing.</li>
            <li>
              More than 5 years of experience working in
              software/internet/fin-tech companies as marketing manager role.
            </li>
            <li>Passion for blockchain technology.</li>
            <li>Experienced working with cross-functional teams.</li>
            <li>Knowledge of conducting go-to-market strategy.</li>
            <li>
              Project management skills with a proven track record of successful
              product launches.
            </li>
            <li>
              Ability to operate and communicate in a fast paced environment and
              to work on multiple projects simultaneously in a dynamic and
              constantly evolving context.
            </li>
          </ul>
        )
      }
    ]
  },
  {
    id: "op15",
    title: "Growth Marketing Manager",
    desc: "This role will be in charge of digital media planning and operations. The candidate is responsible for boosting KryptoGO’s brand awareness globally.",
    tab: ["all", "BD"],
    content: [
      {
        title: "Responsibilities",
        content: (
          <ul>
            <li>
              Leverage data to understand our products in depth, identify areas
              of opportunity, and execute projects to drive user growth,
              retention, engagement, and/or revenue of KryptoGO products.
            </li>
            <li>
              Drive projects focusing on new user growth, mobile usage, website
              and product usage; Inform, influence, support, and execute our
              growth strategy.
            </li>
            <li>
              Work closely with product, engineering, design, and data teams.
            </li>
            <li>
              Collaborate with channel-specific marketing teammates (blogging,
              email, social media, etc.) to identify areas for growth.
            </li>
            <li>
              Test and optimize marketing tactics for customer acquisition (e.g.
              online advertising, direct mail, out of home, affiliate) and
              conversion rate optimization.
            </li>
            <li>
              Conduct and present competitive analysis and market researches.
            </li>
          </ul>
        )
      },
      {
        title: "Requirements",
        content: (
          <ul>
            <li>
              2 years experiences of online marketing/digital advertising.
            </li>
            <li>2 years experiences of app/ website optimisation.</li>
            <li>
              Being data-driven and kind of a data-geek: clear understanding of
              data, analytics, metrics and statistics.
            </li>
            <li>
              Familiar with social media landscape, and how to grow and engage
              the audience and performance on Facebook, Twitter, IG, YouTube
              etc, through content optimization.
            </li>
            <li>Familiar with SEO and ASO.</li>
            <li>
              Hands on experience with Web/App analytics and A/B testing tools.
            </li>
            <li>
              Being social and understanding users’ behavior. Having great
              customer relations skills and being focus on customer experience.
            </li>
            <li>
              Communication skills and ability to manage a project or product.
            </li>
            <li>Ability to deal with ambiguity.</li>
          </ul>
        )
      }
    ]
  },
  {
    id: "op16",
    title: "Executive Assistant",
    desc: "We are looking for a suitable CEO Executive Assistant who is equipped with great people management, cross-department communication, and project management skills.",
    tab: ["all", "BD"],
    content: [
      {
        title: "Foreigners are welcome",
        content: (
          <ul>
            <li>
              We are looking for a suitable CEO Executive Assistant who is
              equipped with great people management, cross-department
              communication, and project management skills. The candidate will
              collaborate with all departments to ensure the company operation
              is operating smoothly and problem solve related blocking issues.
              As the nature of the industry and business changes significantly
              and rapidly, the candidate must keep pace with the CEO, and
              foresee upcoming trends and alteration of industry and business
              events and demands. The candidate will work with the CEO to shadow
              him and to execute or deliberate corporate strategies. Being keen,
              detail oriented, highly flexible, and taking the initiative in
              overseeing the overall operation is critical for success in this
              position. We are looking for a candidate who has great structural
              and management skills, and someone who has previous experiences in
              the blockchain, RegTech, or FinTech industry are preferred to
              accelerate our business growth. The ideal candidate will have
              strong soft skills, be both result and people oriented, and
              comfortable working in a collaborative environment with multiple
              roles at times. Besides the general administration work, the
              candidate will also be responsible and trusted to handle company
              sensitive information.
            </li>
          </ul>
        )
      },
      {
        title: "Responsibilities",
        content: (
          <ul>
            <li>
              Acting as the point of contact between the CEO, stakeholders,
              internal colleagues, and external business partners
            </li>
            <li>
              Deliberating and assisting with fundraising strategies and
              relevant activities
            </li>
            <li>
              Overseeing and managing the operation system, and looking for ways
              to improve the current systems or procedures
            </li>
            <li>
              Attending public relations and professional events on behalf of
              the CEO and establish personal networks to build strong
              relationships with important prospects (investors, partners,
              clients, and vendors)
            </li>
            <li>
              Maintaining professional and technical knowledge by attending
              educational workshops, and reviewing professional publications
            </li>
            <li>Routing corresponding information and delegating tasks</li>
            <li>
              Conducting project researches, planning, reports, and
              presentations
            </li>
            <li>
              Taking a concurrent position as marketing specialist framing brand
              identity and increasing exposure to the targeted audiences
            </li>
            <li>
              Taking a concurrent position as HR to hire, train and develop
              individual employees and conduct team building
            </li>
            <li>
              Develop overall marketing strategies and tactics and drive
              qualified traffic to the front door
            </li>
            <li>
              Monitoring and reporting to the CEO on financial statements and
              monthly cashflow
            </li>
          </ul>
        )
      },
      {
        title: "Requirements",
        content: (
          <ul>
            <li>Bachelor’s degree in Business Administration is preferred</li>
            <li>
              More than 3 years of experience as an Executive Assistant,
              Personal Assistant, Project Manager, or a similar role
            </li>
            <li>
              Has experience in the software/internet/RegTech/FinTech industry
            </li>
            <li>Passion for blockchain technology and RegTech</li>
            <li>
              Ability to take on multiple roles and dynamic projects
              simultaneously and to prioritize tasks
            </li>
            <li>
              Has the ability to communicate report clearly and efficiently with
              logic
            </li>
            <li>Can be both detail oriented and big picture thinking</li>
            <li>Experienced working with cross-functional teams</li>
            <li>
              Project management skills with a proven track record of successful
              product or project launches
            </li>
            <li>Familiarity with office gadgets and applications is a bonus</li>
          </ul>
        )
      }
    ]
  },
  {
    id: "op17",
    title: "Senior Full Stack Developer",
    desc: "Lead the development and maintenance of web-based blockchain projects, ensuring high performance and adherence to Web3 principles.",
    tab: ["all", "Developer"],
    content: [
      {
        title: "Responsibilities",
        content: (
          <ul>
            <li>
              Lead the development and maintenance of web-based blockchain
              projects, ensuring high performance and adherence to Web3
              principles.
            </li>
            <li>
              Collaborate with multidisciplinary teams to bring innovative Web3
              projects to fruition, with a focus on blockchain and
              cryptocurrency applications.
            </li>
            <li>
              Develop and maintain internal tools, emphasizing web frameworks
              and React-based design systems tailored for Web3 functionalities.
            </li>
            <li>
              Create and maintain documentation for all projects, ensuring clear
              understanding and ease of use for both internal teams and the
              wider developer community.
            </li>
            <li>
              Oversee KryptoGO’s open-source initiatives on GitHub, fostering a
              collaborative environment and ensuring the highest code quality.
            </li>
            <li>
              Facilitate communication within the team to optimize workflows and
              project outcomes.
            </li>
            <li>
              Design, develop, and maintain secure, efficient, and scalable
              code, prioritizing blockchain security and smart contract
              reliability.
            </li>
            <li>
              Identify technical challenges and security concerns, implementing
              solutions that strengthen Web3 project integrity.
            </li>
            <li>
              Champion code quality, organization, and automation, with a focus
              on blockchain technology’s unique requirements.
            </li>
            <li>
              Provide technical mentorship to team members, promoting a culture
              of innovation and continuous improvement.
            </li>
          </ul>
        )
      },
      {
        title: "Job Requirements",
        content: (
          <ul>
            <li>
              5+ years of Full Stack / Web Development experience, with a
              significant focus on ReactJS, NodeJs (Typescript), and blockchain
              technologies.
            </li>
            <li>
              Proficient in blockchain software and database architecture, smart
              contracts, and Web3 technologies.
            </li>
            <li>
              Experience with frontend libraries, design systems, Git, Docker,
              NextJS, Vercel, Netlify, MySQL, MongoDB, and understanding of
              blockchain data structures.
            </li>
            <li>
              Committed to KryptoGO’s mission of advancing blockchain technology
              and ensuring secure, efficient digital transactions.
            </li>
            <li>
              Bonus for experience in remote work, open-source contributions,
              and creative coding in the blockchain context.
            </li>
          </ul>
        )
      },
      {
        title: "Portfolio Requirements",
        content: (
          <ul>
            <li>
              Development of decentralized applications (DApps) showcasing smart
              contract integration, user interaction, and blockchain data
              handling.
            </li>
            <li>
              Contributions to open-source blockchain projects or tools, with
              links to GitHub repositories.
            </li>
            <li>
              Implementation of Web3 technologies in commercial or personal
              projects, especially those involving digital asset management, NFT
              platforms, or decentralized finance (DeFi) solutions.
            </li>
            <li>
              Experience in developing secure, scalable blockchain networks or
              contributing to the development of blockchain protocols.
            </li>
            <li>
              Creative coding projects in the Web3 space, such as generative art
              NFTs, blockchain-based games, or innovative use of smart
              contracts.
            </li>
          </ul>
        )
      }
    ]
  },
  {
    id: "op18",
    title: "Senior Front-End Engineer",
    desc: "Design, develop, and maintain modern front-end applications",
    tab: ["all", "Developer"],
    content: [
      {
        title: "Responsibilities",
        content: (
          <ul>
            <li>
              Design, develop, and maintain modern front-end applications.
            </li>
            <li>
              Work with design team to ensure the best user experience and
              website performance
            </li>
            <li>
              Use best front-end development practices to create efficient and
              scalable code
            </li>
            <li>
              Improve the maintainability, scalability and code quality of
              existing applications, including UI components, CI/CD flow
            </li>
            <li>
              Build KryptoGO UI components and work with design team to improve
              the workflow
            </li>
            <li>
              Write UI test and end-to-end test to improve overall quality and
              enhance testability
            </li>
            <li>
              Ensure the website has optimal performance on various browsers and
              devices
            </li>
          </ul>
        )
      },
      {
        title: "Job Requirements",
        content: (
          <ul>
            <li>5+ years of front-end development experience</li>
            <li>
              Proficient in modern front-end frameworks such as React, Next JS
            </li>
            <li>
              Familiar with back-end technologies such as RESTful APIs, Web
              socket, Server sent events
            </li>
            <li>
              Proficient in front-end technologies such as Typescript, styled
              component, Tailwind
            </li>
            <li>Familiar with several design patterns</li>
            <li>
              Familiar with unit test, UI test, e2e test tools such as Jest,
              Mocha, Chai, Cypress, Browserstack, PlayWright
            </li>
            <li>Excellent communication and teamwork skills</li>
            <li>Experience with CMS system</li>
          </ul>
        )
      },
      {
        title: "Preferred Qualifications",
        content: (
          <ul>
            <li>
              Experience with related technologies such as Webpack, esbuild,
              babel, React 18, React server component, Nextjs app directory
            </li>
            <li>
              Experience with monorepo related technologies such as Nx,
              Turborepo
            </li>
            <li>Experience with continuous integration and deployment</li>
            <li>Experience with front-end performance optimization, SEO</li>
            <li>Experience with Docker</li>
            <li>
              Experience with Blockchain: ethers.js, smart contract development
            </li>
          </ul>
        )
      }
    ]
  }
]

const TAB_DATA_en = [
  {
    id: "Box1",
    title: "opening.tabBtn.1",
    tab: "1",
    content: <OpCards data={OPDATA_en} filer="all" />
  },
  {
    id: "Box2",
    title: "opening.tabBtn.2",
    tab: "2",
    content: <OpCards data={OPDATA_en} filer="Developer" />
  },
  {
    id: "Box3",
    title: "opening.tabBtn.3",
    tab: "3",
    content: <OpCards data={OPDATA_en} filer="BD" />
  }
]
const TAB_DATA_cn = [
  {
    id: "Box1",
    title: "opening.tabBtn.1",
    tab: "1",
    content: <OpCards data={OPDATA_en} filer="all" />
  },
  {
    id: "Box2",
    title: "opening.tabBtn.2",
    tab: "2",
    content: <OpCards data={OPDATA_en} filer="Developer" />
  },
  {
    id: "Box3",
    title: "opening.tabBtn.3",
    tab: "3",
    content: <OpCards data={OPDATA_en} filer="BD" />
  }
]
const TAB_DATA_tw = [
  {
    id: "Box1",
    title: "opening.tabBtn.1",
    tab: "1",
    content: <OpCards data={OPDATA_en} filer="all" />
  },
  {
    id: "Box2",
    title: "opening.tabBtn.2",
    tab: "2",
    content: <OpCards data={OPDATA_en} filer="Developer" />
  },
  {
    id: "Box3",
    title: "opening.tabBtn.3",
    tab: "3",
    content: <OpCards data={OPDATA_en} filer="BD" />
  }
]

export default function JoinUsSection() {
  const { locale } = useRouter()
  const { t } = useTranslation("joinUs")
  return (
    <Wrapper id="opening">
      <Heading
        mb="0"
        className="text-gradient text-glow"
        content={t("opening.title")}
      />
      {/* <TabSection dataId={TAB_DATA} /> */}
      {locale === "en" && <TabSection dataId={TAB_DATA_en} />}
      {locale === "cn" && <TabSection dataId={TAB_DATA_cn} />}
      {locale === "tw" && <TabSection dataId={TAB_DATA_tw} />}
      <Button
        content={t("joinUs.btn2")}
        href="https://kryptogo.teamdoor.io/"
        target="blank"
      />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  margin-top: -84px;
  padding-top: 84px;
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 50px;
  align-items: center;
  justify-content: center;
  h2 {
    text-align: center;
  }
`
